import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useAttributesStateStore } from '@/components/datamodel/hooks/useAttributesStore';
import { logger } from '@/services/logger';
import { DataModel, DataModelAttribute, DataModelAttributeTypeName } from '@/types/DataModel';

export const useVisibleMemberAttributes = (
    attribute: DataModelAttribute,
    dataModel: DataModel | undefined,
): DataModelAttribute[] => {
    const memberAttributes = useMemo(
        () => getMemberAttributes(attribute, dataModel) || [],
        [attribute, dataModel],
    );

    const hiddenStates = useAttributesStateStore(
        useShallow((state) =>
            memberAttributes.map((memberAttribute) =>
                (state.hidden[memberAttribute.name] ?? false) || !!memberAttribute.additionalParams?.hidden,
            ),
        ),
    );

    return useMemo(
        () => memberAttributes.filter((_, index) => !hiddenStates[index]),
        [memberAttributes, hiddenStates],
    );
};

export const getMemberAttributes = (
    attribute: DataModelAttribute,
    dataModel: DataModel | undefined,
) : DataModelAttribute[] | null => {

    let memberAttributeNames: string[] = [];

    switch (attribute.typeName) {
        case DataModelAttributeTypeName.MultiDimensional:

            // For multi dimensional, the composite member attributes (columns) are both key attributes
            // and value attributes
            memberAttributeNames = (attribute.keyMembers || []).concat(attribute.valueMembers || []);
            return getAttributes(attribute, memberAttributeNames, dataModel);

        case DataModelAttributeTypeName.MultiReference: {

            // For multi reference, the composite member attributes (columns) are the member attributes
            // of the referenced items defined in `filter` in addition to the transient attributes.
            const referencedItemAttributeNames : string[] = (attribute.params?.filter || attribute.params?.referenceItemAttributes || []) as string[] ;
            const referencedItemAttributes =
                getAttributes(attribute, referencedItemAttributeNames, dataModel)
                    // Referenced item attributes are readonly. If the user
                    // wants to edit them they should edit the referenced item directly.
                    .map(attribute => ({
                        ...attribute,
                        // We take advantage of the `additionalParams` to mutate the
                        // attribute and mark it as readonly.
                        additionalParams: {
                            ...attribute.additionalParams,
                            readonly: true
                        }
                    })) as DataModelAttribute[];

            const valueAttributeName = attribute.params?.valueAttribute;
            const transientAttributeNames : string [] = dataModel?.attribute(valueAttributeName || '')?.members || [];
            const transientAttributes =
                getAttributes(attribute, transientAttributeNames, dataModel)
                    .map(attribute => {
                        if (dataModel?.isMetaAttribute(attribute.name)) {
                            return ({
                                ...attribute,
                                additionalParams: {
                                    ...attribute.additionalParams,
                                    readonly: true
                                }
                            });
                        } else {
                            return attribute;
                        }
                    });

            return referencedItemAttributes.concat(transientAttributes);
        }

        case DataModelAttributeTypeName.Collection:
        case DataModelAttributeTypeName.Group:

            // For Collection and Group attributes, the members (columns) are in the defined composite
            // value attribute
            memberAttributeNames = dataModel?.attribute(attribute.params?.valueAttribute || '')?.members as  string[];
            return getAttributes(attribute, memberAttributeNames, dataModel);

        default:
            logger.error(`useMemberAttributes: Attribute ${attribute.name}, does not support having member attributes`);
            return [] as DataModelAttribute[];
    }
};

/**
 * Given a list of attribute names, returns the attributes' objects.
 */
const getAttributes = (parentAttribute: DataModelAttribute, memberAttributeNames: string[], dataModel: DataModel | undefined) => {
    return memberAttributeNames?.reduce(
        (list: DataModelAttribute[], memberAttributeName: string) => {
            const memberAttribute = dataModel?.attribute(memberAttributeName);
            if (memberAttribute) {
                list.push(memberAttribute);
            } else {
                logger.warn(
                    `useMemberAttributes: Member attribute ${memberAttributeName} of attribute ${parentAttribute.name} was not found, skipping`,
                );
            }

            return list;
        },
        [] as DataModelAttribute[],
    );
};
